import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check, Clock } from 'lucide-react';
import CountUp from '../../components/CountUp';

const howTheyWork = [
  'Contextual targeting by category and intent',
  'Geographic targeting by country, region, or city',
  'Appears inside the ChatGPT response itself',
  'No separate landing page required — can link directly',
  'Performance tracked by impressions, clicks, and conversions'
];

const status = [
  { text: 'Self-serve and open to all US businesses — live now', done: true },
  { text: 'No minimum spend; CPC and CPM bidding available', done: true },
  { text: 'Live in the US, Canada, Australia and New Zealand', done: true },
  { text: 'UK, Japan, Brazil and more rolling out now', done: false },
  { text: 'EU pending GDPR consent requirements', done: false }
];

const stats = [
  { value: '900M+', label: 'Weekly ChatGPT users (OpenAI, Feb 2026) — more than Twitter, LinkedIn, and Pinterest combined' },
  { value: '#1', label: 'Fastest-growing consumer search interface in history' },
  { value: '$0', label: 'Cost-per-click on organic AI results today — paid access is what locks in prime position' }
];

const steps = [
  { num: '01', title: 'Access & Setup', desc: 'We onboard your agency to the ChatGPT Ads platform and configure your first client account.' },
  { num: '02', title: 'Campaign Build', desc: "We build the campaign — targeting, copy, bidding strategy — based on your client's objectives and market." },
  { num: '03', title: 'Live Management', desc: 'We monitor and optimise daily. You receive weekly performance updates to share with clients under your brand.' },
  { num: '04', title: 'Monthly Reporting', desc: 'White-label monthly report: impressions, clicks, conversions, spend efficiency. Your agency name on every page.' }
];

const altBg = { background: 'rgba(74, 53, 128, 0.07)' };

const RankChatGptAdsPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--rank-bg)' }} data-testid="rank-chatgpt-ads-page">
      <Helmet><title>MarqiRank — ChatGPT Ads (Now Live)</title></Helmet>

      {/* Hero */}
      <section className="py-24 md:py-32 text-center">
        <div className="container max-w-4xl">
          <span className="inline-block px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider bg-[var(--accent-bg)] text-[var(--accent-primary)] mb-8" data-testid="chatgpt-ads-status-badge">
            Now Live · Self-Serve · No Minimum Spend
          </span>
          <h1 className="display-md text-[var(--text-primary)] mb-6" data-testid="rank-chatgpt-ads-hero-title">
            The Newest Advertising Channel in the World.
            <br /><span className="text-[var(--rank-accent)]">Your Agency Can Offer It Now.</span>
          </h1>
          <p className="body-lg mb-12 max-w-3xl mx-auto">
            ChatGPT Ads are paid placements inside ChatGPT responses. When someone asks ChatGPT for a
            recommendation and your client has an active campaign, their business appears as a sponsored
            result — contextually matched, geographically targeted, inside the world’s fastest-growing
            search interface. 900M+ weekly users. Your clients can be there.
          </p>
          <p className="body-md mb-12 max-w-2xl mx-auto text-[var(--text-muted)]">
            Available to most US business categories. Some regulated sectors — financial services, health,
            and political — are currently restricted by OpenAI. We'll confirm your client's eligibility before anything goes live.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact" className="btn-primary" data-testid="rank-chatgpt-ads-cta-primary">
              Get Started with ChatGPT Ads
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link to="/rank/geo" className="btn-secondary">Learn About Organic Ranking First</Link>
          </div>
        </div>
      </section>

      {/* What they are */}
      <section className="py-24" style={altBg}>
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16 max-w-3xl mx-auto">Ads Inside AI Answers. Not Beside Them.</h2>
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="feature-card">
              <h3 className="h3 text-[var(--text-primary)] mb-6">How They Work</h3>
              <p className="body-md mb-6">ChatGPT Ads appear contextually inside AI responses — not in a sidebar, not as a banner. When a user’s query matches your client’s campaign targeting, a sponsored result appears naturally within the answer.</p>
              <ul className="space-y-3 mb-6">
                {howTheyWork.map((h) => (
                  <li key={h} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-[var(--rank-accent)] flex-shrink-0 mt-0.5" />
                    <span className="text-[var(--text-secondary)]">{h}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-[var(--text-muted)]">OpenAI is still rapidly evolving the ad product. Marqi manages campaigns end-to-end, so your agency gets the upside of a brand-new channel without having to learn a brand-new platform.</p>
            </div>
            <div className="space-y-8">
              <div className="feature-card">
                <h3 className="h3 text-[var(--text-primary)] mb-6">Current Status by Market</h3>
                <ul className="space-y-4">
                  {status.map((s) => (
                    <li key={s.text} className="flex items-start space-x-3">
                      {s.done
                        ? <Check className="w-5 h-5 text-[var(--rank-accent)] flex-shrink-0 mt-0.5" />
                        : <Clock className="w-5 h-5 text-[var(--rank-amber)] flex-shrink-0 mt-0.5" />}
                      <span className={s.done ? 'text-[var(--text-secondary)]' : 'text-[var(--rank-amber)]'}>{s.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="feature-card">
                <h3 className="h3 text-[var(--text-primary)] mb-4">Why Move Now</h3>
                <p className="body-md">ChatGPT Ads is live and open — but most agencies haven’t realised it yet. The ones who bring it to clients first own the conversation. Marqi runs the campaigns end-to-end, so you can offer it from day one without building anything.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why it matters */}
      <section className="py-24">
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16">The Scale of the Opportunity.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((s, i) => (
              <div key={s.label} className="text-center animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                <CountUp value={s.value} className="block text-6xl font-bold text-[var(--rank-accent)] mb-4" />
                <p className="body-md max-w-xs mx-auto">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How Marqi manages it */}
      <section className="py-24" style={altBg}>
        <div className="container">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="h1 text-[var(--text-primary)] mb-6">We Handle the Complexity. You Present the Results.</h2>
            <p className="body-lg">ChatGPT Ads is live, but still a fast-moving platform with evolving formats, targeting logic, and policies. Marqi manages the full execution under your agency brand.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((s, i) => (
              <div key={s.num} className="feature-card animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="text-3xl font-bold text-[var(--rank-accent)] mb-6">{s.num}</div>
                <h3 className="h3 text-[var(--text-primary)] mb-3">{s.title}</h3>
                <p className="body-md">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container">
          <div className="feature-card text-center max-w-4xl mx-auto">
            <h2 className="h1 text-[var(--text-primary)] mb-6">Be the Agency That Offers ChatGPT Ads Before Anyone Else Does.</h2>
            <p className="body-lg mb-12 max-w-2xl mx-auto">Book a session and we’ll show you what’s live today, what a campaign looks like for your client’s market, and how Marqi runs it end-to-end under your brand.</p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact" className="btn-primary glow-effect">Get Started with ChatGPT Ads<ArrowRight className="ml-2 w-5 h-5" /></Link>
              <Link to="/rank/geo" className="btn-secondary">Learn About GEO & AEO First</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RankChatGptAdsPage;
